import React from 'react';
import get from 'lodash.get';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import {
  Element,
  FontType,
  Color,
  Typography
} from '@americanexpress/aqx-components';
import ColorPicker, { parent } from 'src/universal/components/ColorPicker/index.js';
import leftRailStyles from 'page_modules/business-pdp/components/StickyLeftRail/__styles__/index.scss?modules';
import removeTagsAndTheirContent from 'service_modules/utils/text-remove-tags';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { getUpgradeCtaProps } from 'src/universal/utils/orchestraUtils';
import { buildCtaList, buildDisclaimerList } from './index';
import DynamicApLeftRail from 'page_modules/business-pdp/components/StickyLeftRail/DynamicAppLeftRail/index.js';
import dynamicAppStyles from 'page_modules/business-pdp/components/StickyLeftRail/DynamicAppLeftRail/__styles__/index.scss?modules';
import YourCardBadge from 'src/universal/components/YourCardBadge';
import AmazonDisclaimer from '../../business-vac/components/AmazonDisclaimer';

/* eslint-disable complexity */
export const buildStickySideRailProps = ({
  cardData = {},
  labels = {},
  links = {},
  cardInfo = {},
  dispatch,
  pznOfferList,
  isSummit = false,
  cardArtUrl,
  annualFeeDisclaimer,
  showDynamicApp,
  isG171
}) => {
  let ctaList = buildCtaList({
    cardInfo,
    links,
    dispatch,
    labels,
    isLeftRail: true,
    isG171
  });

  const { needUpgradeCta } = getUpgradeCtaProps(
    links,
    get(cardInfo, 'currentCard.pznOffer') || {},
    cardInfo.isGCSMember || cardInfo.isGCSLegacy
  );

  const disclaimerList = buildDisclaimerList(
    links,
    'LeftRail',
    needUpgradeCta,
    cardInfo
  );

  const feeEle = (
    <Element Component="h2" className={leftRailStyles.feeHeader}>
      <Sanitizer Component="span" safelySetInnerHTML={labels.annualFee} />
      <Sanitizer
        Component="span"
        safelySetInnerHTML={(get(cardData, 'annualFee') || '').toLowerCase()}
      />
      {annualFeeDisclaimer && (
        <Sanitizer
          Component="div"
          className={leftRailStyles.annualFeeDisclaimer}
          safelySetInnerHTML={annualFeeDisclaimer}
        />
      )}
    </Element>
  );

  const body = [{ description: feeEle }];

  const headline = get(cardData, 'cardHeadlines.sideRail');
  const aprHeader =
    parseArrayToObject(get(cardData, 'aprHeaders') || [], 'identifier').sideRail ||
    {};
  let aprDescription = get(pznOfferList, '[0].rate') || '';

  if (headline) {
    body.unshift({ title: headline });
  }

  showDynamicApp &&
    body.push({
      title: '',
      description: (
        <DynamicApLeftRail
          styles={dynamicAppStyles}
          disclaimerList={disclaimerList}
          ctaProps={ctaList}
          hasUpgradeCta={needUpgradeCta}
        />
      )
    });

  body.unshift({
    description: (
      <ColorPicker shortName={cardData.shortName} parentType={parent.SIDE_RAIL} />
    ),
    title: ''
  });

  const titleText =
    cardData?.cardHeadlines?.sideRailTitle ||
    cardData?.cardHeadlines?.longTitle ||
    '';

  const combinedEle = (
    <React.Fragment>
      <h1>
        <Sanitizer safelySetInnerHTML={titleText} />
      </h1>
      <YourCardBadge shortName={cardData.shortName} isPdp={true} />
    </React.Fragment>
  );

  const headerProps = { Component: 'span', title: combinedEle };

  const data = {
    sideRail: {
      cardUrl: cardArtUrl || '',
      cardArtAlt: removeTagsAndTheirContent(
        get(cardData, 'cardHeadlines.summitCardAlt') ||
          get(cardData, 'cardHeadlines.title') ||
          ''
      ),
      header: headerProps,
      body
    },
    ctas: showDynamicApp ? [] : ctaList,
    disclaimerLinks: showDynamicApp ? {} : disclaimerList,
    contentAfterCTAComponent: showDynamicApp ? (
      <></>
    ) : (
      <Typography
        Component="h3"
        $family={FontType.BentonRegular}
        $size={18}
        $lineHeight={'120%'}
        theme={{ marginBottom: 0 }}
      >
        <Sanitizer safelySetInnerHTML={labels.getDecision} />
        <AmazonDisclaimer shortName={cardData.shortName} isLeftRail={true} />
      </Typography>
    )
  };

  if (aprHeader.text && aprDescription && !showDynamicApp) {
    // temporary fix to correct compliance issue on PC
    const needsBreak =
      parseArrayToObject(get(cardData, 'aprHeaders') || [], 'identifier')
        .needsBreak || {};
    const replaceWithBreak =
      parseArrayToObject(get(cardData, 'aprHeaders') || [], 'identifier')
        .replaceWithBreak || {};

    if (isSummit && needsBreak && navigator.userAgent.indexOf('Win') != -1) {
      aprDescription = aprDescription.replace(
        needsBreak.text,
        replaceWithBreak.text
      );
    }
    data.contentBeforeCTAComponent = (
      <Typography
        $family={FontType.HelveticaBold}
        $size={16}
        $lineHeight={24}
        theme={{ marginBottom: 10, color: Color.Black }}
      >
        <Sanitizer Component="h3" safelySetInnerHTML={aprHeader.text} />
        <Typography
          $family={FontType.HelveticaRegular}
          $size={12}
          $lineHeight={18}
          className={leftRailStyles.aprDescription}
        >
          {aprDescription}
        </Typography>
      </Typography>
    );
  }

  return {
    data,
    customization: {
      stickyTopOffset: 60,
      theme: {
        cardTitleTheme: { paddingTop: 0 },
        feeElementTheme: {
          typographyTheme: { margin: 0 },
          bodyTheme: {
            paddingTop: 0,
            marginBottom: isSummit ? 20 : 0,
            color: isSummit ? Color.Black : Color.DeepBlue
          },
          titleTheme: { color: Color.Black }
        }
      }
    }
  };
};
